import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';

export function formatTrainingDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString();
}

export function buildLast12Months() {
  const out = [];
  const now = new Date();
  const firstOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  for (let i = 11; i >= 0; i -= 1) {
    out.push(
      new Date(firstOfThisMonth.getFullYear(), firstOfThisMonth.getMonth() - i, 1),
    );
  }
  return out;
}

export function buildMonthsForCalendarYear(year) {
  return Array.from({ length: 12 }, (_, month) => new Date(year, month, 1));
}

/** Years with at least one event, plus current year; newest first. */
export function getAvailableActivityYears(visibleEvents) {
  const years = new Set([new Date().getFullYear()]);

  for (const e of visibleEvents) {
    const dt = e?.startTime ? new Date(e.startTime) : null;
    if (!dt || Number.isNaN(dt.getTime())) continue;
    years.add(dt.getFullYear());
  }

  return Array.from(years).sort((a, b) => b - a);
}

/** Monthly activity for a calendar year; X labels are month names only (no year). */
export function buildActivityForYear(visibleEvents, year) {
  const months = buildMonthsForCalendarYear(year);
  const buckets = new Map();
  for (const m of months) {
    buckets.set(`${m.getFullYear()}-${m.getMonth()}`, 0);
  }

  for (const e of visibleEvents) {
    const dt = e?.startTime ? new Date(e.startTime) : null;
    if (!dt || Number.isNaN(dt.getTime()) || dt.getFullYear() !== year) continue;
    const key = `${dt.getFullYear()}-${dt.getMonth()}`;
    buckets.set(key, (buckets.get(key) || 0) + 1);
  }

  const labels = months.map((d) => format(d, 'MMM', { locale: enUS }));
  const values = months.map(
    (d) => buckets.get(`${d.getFullYear()}-${d.getMonth()}`) || 0,
  );

  return { labels, values };
}

export function buildActivityByMonth(visibleEvents, last12Months) {
  const buckets = new Map();
  for (const m of last12Months) {
    buckets.set(`${m.getFullYear()}-${m.getMonth()}`, 0);
  }

  for (const e of visibleEvents) {
    const dt = e?.startTime ? new Date(e.startTime) : null;
    if (!dt || Number.isNaN(dt.getTime())) continue;
    const key = `${dt.getFullYear()}-${dt.getMonth()}`;
    if (!buckets.has(key)) continue;
    buckets.set(key, (buckets.get(key) || 0) + 1);
  }

  const labels = last12Months.map((d) => format(d, 'MMM yyyy', { locale: enUS }));
  const values = last12Months.map(
    (d) => buckets.get(`${d.getFullYear()}-${d.getMonth()}`) || 0,
  );

  return { labels, values };
}

export function filterRecentVisibleEvents(visibleEvents, last12Months) {
  if (!last12Months.length) return [];
  const start = last12Months[0];
  const now = new Date();
  return visibleEvents.filter((e) => {
    const dt = e?.startTime ? new Date(e.startTime) : null;
    if (!dt || Number.isNaN(dt.getTime())) return false;
    return dt >= start && dt <= now;
  });
}

export const EVENTS_YEAR_FILTER_ALL = 'all';

export function filterEventsForNameBars(visibleEvents, yearFilter) {
  if (yearFilter === EVENTS_YEAR_FILTER_ALL || yearFilter == null || yearFilter === '') {
    return visibleEvents;
  }
  const year = Number(yearFilter);
  if (!Number.isFinite(year)) return visibleEvents;
  return visibleEvents.filter((e) => {
    const dt = e?.startTime ? new Date(e.startTime) : null;
    if (!dt || Number.isNaN(dt.getTime())) return false;
    return dt.getFullYear() === year;
  });
}

function buildEventNameBarsFromEvents(events) {
  const counts = new Map();
  for (const e of events) {
    const name = (e?.eventName || '').trim();
    if (!name) continue;
    counts.set(name, (counts.get(name) || 0) + 1);
  }

  const entries = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  return {
    labels: entries.map(([k]) => k),
    values: entries.map(([, v]) => v),
  };
}

/** Dashboard: all years or one calendar year. */
export function buildEventNameBarsForYearFilter(visibleEvents, yearFilter) {
  return buildEventNameBarsFromEvents(filterEventsForNameBars(visibleEvents, yearFilter));
}

/** Roster file: last 12 months window. */
export function buildEventNameBars(visibleEvents, last12Months) {
  const recent = filterRecentVisibleEvents(visibleEvents, last12Months);
  return buildEventNameBarsFromEvents(recent);
}
